/**
 * Строки под ногой карточки, которые появляются только по причине в этой вилке.
 *
 * Практик ставит «маленький банк» и шума не терпит (28.09): строки без повода
 * здесь нет — либо у ноги есть конкретный риск, либо функция молчит.
 */

import { Decimal } from 'decimal.js';

import { voidPnl } from '../lib/calc';
import { marketLabel, type Fork } from '../lib/protocol';
import type { RowLeg } from '../lib/row';
import { polyEventSlug, polyPinnedMarket } from '../poly/coupon';

/**
 * Площадки, чей рынок при несостоявшемся матче или карте решается 50/50.
 * Predict.fun не входит: в его правилах такого пункта не нашли.
 */
const HALF_ON_VOID = new Set(['polymarket', 'limitless']);

export interface VoidNote {
  text: string;
  /** Короткая строка на плитке рядом со стаканом. */
  short: string;
  /** Цена выше 50¢: при отмене нога в минусе. */
  risk: boolean;
  /** Отмена в этой вилке вероятнее обычного. */
  loud: boolean;
}

/** «при отмене: −$X — риск ваш» / «при отмене: +$X». */
export function voidNote(leg: RowLeg, fork: Fork): VoidNote | null {
  if (!HALF_ON_VOID.has(leg.source) || leg.unit !== 'share' || leg.lay) return null;
  if (leg.usd === null || !(leg.usd > 0) || leg.cents === null || !(leg.cents > 0)) return null;
  const pnl = voidPnl(new Decimal(leg.usd.toFixed(2)), new Decimal(String(leg.cents)).div(100));
  if (pnl.isZero()) return null;
  const loud =
    (fork.sport ?? leg.sport) === 'baseball' ||
    (fork.rl ?? []).some((rule) => rule.c === 'map_may_skip');
  const risk = pnl.isNegative();
  const money = pnl.abs().toFixed(2);
  return {
    text: risk ? `при отмене: −$${money} — риск ваш` : `при отмене: +$${money}`,
    short: risk ? `отмена −$${money}` : `отмена +$${money}`,
    risk,
    loud,
  };
}

/**
 * Нога Polymarket без выбранного рынка: страница события откроет «Match
 * Winner», а вилка может стоять на карте или тотале (#56, #86).
 */
export function marketNote(leg: RowLeg, fork: Fork): string | null {
  if (leg.source !== 'polymarket' || leg.url === null) return null;
  const key = leg.marketKey ?? fork.market;
  if (polyPinnedMarket(leg.url, key, leg.side)) return null;
  let slug: string | null;
  try {
    slug = new URL(leg.url).searchParams.get('marketSlug');
  } catch {
    return null;
  }
  if (slug !== null && slug !== '') return null;
  const parts = (key ?? '').split(':');
  // Победа в матче и есть то, что откроется по умолчанию.
  if (parts[0] === 'WIN' && parts[2] === 'M') return null;
  // Карта N: купон сам ставит `{event}-gameN`, окно не станет матчем.
  if (parts[0] === 'WIN' && /^P[1-9]\d*$/.test(parts[2] ?? '') && polyEventSlug(leg.url)) {
    return null;
  }
  const need = marketLabel(key, fork.sport);
  return `Polymarket откроет «Матч» — нужна «${need}»`;
}

const norm = (who: string[]): string =>
  who
    .map((one) => one.trim().toLowerCase())
    .sort()
    .join('|');

/**
 * Команды так, как их пишет каждая площадка, — когда написания различаются
 * (#93: «nip» не узнан как Ninjas in Pyjamas).
 */
export function namesNote(legs: RowLeg[], nameOf: (code: string) => string): string | null {
  const said = legs.filter((leg) => leg.who.length > 0);
  if (said.length < 2) return null;
  const first = norm(said[0]!.who);
  if (said.every((leg) => norm(leg.who) === first)) return null;
  return said.map((leg) => `${nameOf(leg.source)}: ${leg.who.join(' – ')}`).join(' · ');
}

/**
 * «Процент выше, чем у 99% вилок этой пары за месяц: причина». Такие вилки
 * практика отменялись втрое чаще (#20, #52); строка есть только при `rare`.
 */
export function rareNote(
  fork: Fork,
  legs: readonly RowLeg[],
  nameOf: (code: string) => string,
): string | null {
  const p = fork.rare?.p;
  if (typeof p !== 'number') return null;
  const head = `Процент выше, чем у ${p}% вилок этой пары за месяц`;
  const codes = new Set((fork.rl ?? []).map((rule) => rule.c));
  if (codes.has('map_may_skip')) return `${head}: карта может не состояться`;
  if ((fork.sport ?? legs[0]?.sport) === 'baseball') return `${head}: бывает ничья`;
  const lone = legs.filter((leg) => leg.initiator === true);
  const maker =
    lone.length === 1 ? lone[0]!.source : typeof fork.initiatorBook === 'string' ? fork.initiatorBook : null;
  if (maker !== null && (codes.has('odds_anomaly') || codes.has('no_consensus') || lone.length === 1)) {
    return `${head}: цену сделала ${nameOf(maker)}, её кэф оторван от остальных — такие ставки отменяют`;
  }
  if (codes.has('odds_anomaly') || codes.has('no_consensus')) {
    return `${head}: кэф одной конторы оторван от остальных — такие ставки отменяют`;
  }
  return head;
}
